import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TreatmentCard } from "@/components/treatments/TreatmentCard";
import { HospitalCard } from "@/components/hospitals/HospitalCard";
import { DoctorCard } from "@/components/doctors/DoctorCard";
import { Database } from "@/integrations/supabase/types";

type TreatmentCategory = Database["public"]["Enums"]["treatment_category"];

interface RelatedContentProps {
  treatmentCategories?: TreatmentCategory[];
  treatmentIds?: string[];
  hospitalIds?: string[];
  doctorIds?: string[];
}

export function RelatedContent({
  treatmentCategories = [],
  treatmentIds = [],
  hospitalIds = [],
  doctorIds = [],
}: RelatedContentProps) {
  // Fetch related treatments by category or IDs
  const { data: treatments } = useQuery({
    queryKey: ["related-treatments", treatmentCategories, treatmentIds],
    queryFn: async () => {
      if (treatmentIds.length > 0) {
        const { data } = await supabase
          .from("treatments")
          .select("*")
          .in("id", treatmentIds)
          .limit(3);
        return data || [];
      }
      if (treatmentCategories.length > 0) {
        const { data } = await supabase
          .from("treatments")
          .select("*")
          .in("category", treatmentCategories)
          .limit(3);
        return data || [];
      }
      return [];
    },
    enabled: treatmentCategories.length > 0 || treatmentIds.length > 0,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  // Fetch related hospitals
  const { data: hospitals } = useQuery({
    queryKey: ["related-hospitals", hospitalIds, treatmentCategories],
    queryFn: async () => {
      if (hospitalIds.length > 0) {
        const { data } = await supabase
          .from("hospitals")
          .select("*")
          .in("id", hospitalIds)
          .limit(3);
        return data || [];
      }
      // If no specific hospitals, get featured ones
      if (treatmentCategories.length > 0) {
        const { data } = await supabase
          .from("hospitals")
          .select("*")
          .eq("is_featured", true)
          .limit(3);
        return data || [];
      }
      return [];
    },
    enabled: hospitalIds.length > 0 || treatmentCategories.length > 0,
    staleTime: 1000 * 60 * 60,
  });

  // Fetch related doctors
  const { data: doctors } = useQuery({
    queryKey: ["related-doctors", doctorIds, treatmentCategories],
    queryFn: async () => {
      if (doctorIds.length > 0) {
        const { data } = await supabase
          .from("doctors")
          .select("*")
          .in("id", doctorIds)
          .limit(3);
        return data || [];
      }
      // If no specific doctors, get featured ones matching specialty
      if (treatmentCategories.length > 0) {
        const { data } = await supabase
          .from("doctors")
          .select("*")
          .eq("is_featured", true)
          .limit(3);
        return data || [];
      }
      return [];
    },
    enabled: doctorIds.length > 0 || treatmentCategories.length > 0,
    staleTime: 1000 * 60 * 60,
  });

  const hasContent = 
    (treatments && treatments.length > 0) || 
    (hospitals && hospitals.length > 0) || 
    (doctors && doctors.length > 0);

  if (!hasContent) return null;

  return (
    <aside className="mt-12 pt-8 border-t border-border">
      {/* Related Treatments */}
      {treatments && treatments.length > 0 && (
        <section className="mb-10">
          <h2 className="font-display text-2xl font-bold text-secondary mb-6">
            Related Treatments
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {treatments.map((treatment) => (
              <TreatmentCard key={treatment.id} treatment={treatment} />
            ))}
          </div>
        </section>
      )}

      {/* Related Hospitals */}
      {hospitals && hospitals.length > 0 && (
        <section className="mb-10">
          <h2 className="font-display text-2xl font-bold text-secondary mb-6">
            Top Hospitals for This Treatment
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {hospitals.map((hospital) => (
              <HospitalCard key={hospital.id} hospital={hospital} />
            ))}
          </div>
        </section>
      )}

      {/* Related Doctors */}
      {doctors && doctors.length > 0 && (
        <section className="mb-10">
          <h2 className="font-display text-2xl font-bold text-secondary mb-6">
            Expert Specialists
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {doctors.map((doctor) => (
              <DoctorCard key={doctor.id} doctor={doctor} />
            ))}
          </div>
        </section>
      )}
    </aside>
  );
}
